<template>
  <div class="objetive-container">
    <div v-if="objectives.length != 0">
      <div class="gray-chip ml-3">
        Objetivo(s)
      </div>
      <div
        v-for="(objetive, index) in objectives"
        :key="`objective-${index}`"
        class="objetive-description"
      >
        <div v-if="!objetive.isEdit" class="row align-items-center m-0">
          <div class="col-12 col-md-10 pl-4 text-format">
            <b>{{ `${goalNumber}.${index + 1}` }}</b>
            {{ `${objetive.description}. ${panelText(objetive)}` }}

            <div v-if="!objetive.manageSummary" class="d-md-flex">
              <div
                v-for="(level, idx) in objetive.executionLevelArchieved"
                :key="`level-${idx}`"
              >
                <div
                  class="chip"
                  :style="{
                    'background-color': level.executionLevel.color,
                  }"
                >
                  {{ level.period.name }} -
                  {{ level.executionLevel.name }}
                  <em
                    v-if="!isLevelReadOnly()"
                    class="far fa-times-circle text-danger ml-2"
                    style="cursor: pointer;"
                    @click="deleteLevelHandler(idx, index)"
                  />
                </div>
              </div>
            </div>
          </div>
          <div
            class="actions col-12 col-md-2 mt-3 mt-md-0 pl-0 pr-0 d-md-flex justify-content-end"
          >
            <drop-down
              v-if="!readOnly"
              class="objetive-action"
              :items="actionItems(index, objetive.id)"
            />
            <div v-else-if="!hasExecutionLevelArchieved(objetive)">
              <action-button
                v-if="showSummaryButton(objetive.manageSummary)"
                i="fas fa-clipboard-list"
                text="Informe de Progreso"
                variant="outline-success"
                @click="summaryHandler(index)"
              />
            </div>
          </div>
          <div v-if="objetive.manageSummary" class="col-12 px-0 px-md-3 mt-2">
            <execution-level-form
              :execution-form-options="
                filteredLegend(objetive.executionLevelArchieved)
              "
              @cancel-legend="summaryHandler(index)"
              @save-legend="saveLegendHandler($event, index)"
            />
          </div>
        </div>
        <objetive-form
          v-if="objetive.isEdit"
          :measurement="measurement"
          :model="objetive"
          v-on="$listeners"
          @cancel-update="cancelUpdateHandler(index)"
        />
      </div>
    </div>
    <objetive-form
      v-if="addObjetive"
      :measurement="measurement"
      v-on="$listeners"
    />
  </div>
</template>

<script>
import ObjetiveForm from './ObjetiveForm.vue';
import ExecutionLevelForm from './ExecutionLevelForm.vue';
import interventionPlanMeasure from '@/utils/constants/interventionPlanMeasure.js';
import DropDown from 'dropdown';
import ActionButton from 'ActionButton';

export default {
  name: 'InterventionPlanObjetives',
  components: {
    ObjetiveForm,
    DropDown,
    ExecutionLevelForm,
    ActionButton,
  },
  props: {
    isAchievementSummary: {
      type: Boolean,
      default: () => false,
    },
    summaryReadOnly: {
      type: Boolean,
      default: () => false,
    },
    readOnly: {
      type: Boolean,
      default: () => false,
    },
    legendData: {
      type: Object,
      default: () => {},
    },
    measurement: {
      type: Array,
      default: () => [],
    },
    goalNumber: {
      type: Number,
      default: () => 1,
    },
    data: {
      type: Array,
      default: () => [],
    },
    addObjetive: {
      type: Boolean,
      default: () => false,
    },
    submittedSummaries: {
      type: Array,
      default: () => [],
    },
    academicYearId: {
      type: Number,
      default: () => null,
    },
  },
  data: () => ({
    editingObjetive: false,
    objectives: [],
    objetive: {
      description: null,
      measurement: null,
      measureAmt1: 0,
      measureAmt2: 0,
      percent: null,
    },
  }),
  computed: {
    showSummaryButton() {
      return (formFlag) =>
        this.readOnly &&
        this.isAchievementSummary &&
        !this.summaryReadOnly &&
        !formFlag &&
        this.isCurrentPeriodMatched();
    },
    hasExecutionLevelArchieved() {
      return (objetive) =>
        objetive &&
        objetive.executionLevelArchieved &&
        objetive.executionLevelArchieved.length > 0;
    },
    isCurrentPeriodMatched() {
      return () =>
        this.legendData.period.some(
          (period) =>
            period.periodId === this.currentMonthNumber() &&
            period.academicYearId === this.academicYearId
        );
    },
    filteredLegend() {
      return (executionLevel) =>
        executionLevel
          ? {
              ...this.legendData,
              period: this.legendData.period.filter((e) =>
                executionLevel.every(
                  (item) => item.period.periodId != e.periodId
                )
              ),
            }
          : this.legendData;
    },
    panelText() {
      return (objetive) => {
        const measurement = objetive.measurement?.id || objetive.measureCodeId;
        return measurement == interventionPlanMeasure.Percent
          ? `En el ${objetive.percent || objetive.measureAmt1}% de las veces.`
          : `En ${objetive.measureAmt1} de ${objetive.measureAmt2} intentos.`;
      };
    },
    actionItems() {
      return (index, id) => {
        return [
          {
            name: 'Editar',
            label: 'Editar',
            icon: 'fas fa-edit color-primary-1',
            border: 'bottom',
            click: () => this.editObjetive(index),
          },
          {
            name: 'Eliminar',
            label: 'Eliminar',
            icon: 'fas fa-times-circle color-primary-3',
            click: () => this.deleteObjetive(id),
          },
        ];
      };
    },
  },
  watch: {
    data: {
      immediate: true,
      handler(current) {
        if (current) {
          this.objectives = current.map((v) => ({
            ...v,
            isEdit: false,
            manageSummary: false,
          }));
        }
      },
    },
  },
  methods: {
    currentMonthNumber() {
      return new Date().getMonth() + 1;
    },
    isLevelReadOnly() {
      return (
        Array.isArray(this.submittedSummaries) &&
        this.submittedSummaries.length > 0
      );
    },
    saveLegendHandler(event, idx) {
      const objective = this.objectives[idx];
      objective.executionLevelArchieved
        ? objective.executionLevelArchieved.push(event)
        : (objective.executionLevelArchieved = [{ ...event }]);
      objective.executionLevelArchieved.sort(
        (a, b) => a.period.order - b.period.order
      );
      objective.manageSummary = false;
      this.$emit('edit-objetive', objective);
    },
    deleteLevelHandler(levelIdx, objectiveIdx) {
      this.objectives[objectiveIdx].executionLevelArchieved.splice(levelIdx, 1);
      this.$emit('edit-objetive', this.objectives[objectiveIdx]);
    },
    summaryHandler(idx) {
      this.objectives.forEach((o, index) => {
        o.manageSummary = idx == index ? !o.manageSummary : false;
      });
    },
    deleteObjetive(id) {
      this.$emit('delete-objetive', id);
    },
    cancelUpdateHandler(index) {
      this.objectives[index].isEdit = false;
    },
    editObjetive(index) {
      this.objectives.forEach((o) => {
        o.isEdit = false;
      });
      this.objetive = this.objectives[index];
      this.objectives[index].isEdit = !this.objectives[index].isEdit;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/shared/scss/config/colors.scss';

.objetive-container {
  margin-top: -0.2rem;
  padding: 1rem;
  padding-top: 0;
  padding-left: 1.7rem;
  ::v-deep &__text-area {
    .small-counter {
      font-size: 0.8rem;
      color: map-get($colors, 'gray-6');
    }
  }

  .gray-chip,
  .chip {
    border-radius: 0.9rem;
    padding: 0 0.5rem;
    width: fit-content;
  }

  .chip {
    margin-left: 1rem;
    margin-top: 0.25rem;
    font-weight: bold;
  }

  .gray-chip {
    color: white;
    padding: 0.2rem 0.5rem;
    background-color: map-get($colors, 'gray-2');
  }

  .objetive-description {
    margin-top: 0.5rem;
    margin-bottom: 1rem;
  }

  .text-format {
    overflow-wrap: break-word;
    text-align: justify;
  }

  @media only screen and (max-width: 768px) {
    padding-top: 1rem;
    .chip {
      margin-left: 0;
    }
    ::v-deep .actions {
      .objetive-action {
        .btn {
          width: 100%;
        }
      }
    }
  }
}
</style>
